import { ROLE_PERMISSIONS, type Permission } from "@corefinops/authz";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { ApiClient } from "./client.js";

/**
 * Session context — the signed-in principal (Cognito sub from a verified
 * token), the active tenant, the user's roles there, and a configured
 * ApiClient. Permissions are derived from the same role bundles the API
 * enforces; the UI only uses them to hide/disable controls — the API remains
 * the authority and re-checks every request.
 */

export interface Session {
  readonly userSub: string;
  readonly email?: string | null;
  readonly tenantId: string;
  readonly tenantName?: string;
  readonly roles?: readonly string[];
  /** Static token (local/test mode) or a provider that refreshes on demand. */
  readonly token: string | (() => Promise<string | null>);
  readonly apiBaseUrl: string;
}

interface SessionContextValue {
  readonly session: Session;
  readonly client: ApiClient;
  readonly can: (p: Permission) => boolean;
  readonly signOut?: (() => void) | undefined;
  readonly switchTenant?: (() => void) | undefined;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function permissionsFor(roles: readonly string[] | undefined): ReadonlySet<Permission> {
  const out = new Set<Permission>();
  for (const r of roles ?? []) for (const p of ROLE_PERMISSIONS[r as keyof typeof ROLE_PERMISSIONS] ?? []) out.add(p);
  return out;
}

export function SessionProvider(props: {
  session: Session;
  children: ReactNode;
  onUnauthorized?: () => void;
  signOut?: () => void;
  switchTenant?: () => void;
}): ReactNode {
  const { session } = props;
  const client = useMemo(
    () => new ApiClient({ baseUrl: session.apiBaseUrl, token: session.token, tenantId: session.tenantId, ...(props.onUnauthorized ? { onUnauthorized: props.onUnauthorized } : {}) }),
    [session.apiBaseUrl, session.token, session.tenantId, props.onUnauthorized]
  );
  const perms = useMemo(() => permissionsFor(session.roles), [session.roles]);
  const value = useMemo(
    // Without known roles (local token mode) every control renders; the API still enforces.
    () => ({ session, client, can: (p: Permission) => (session.roles ? perms.has(p) : true), signOut: props.signOut, switchTenant: props.switchTenant }),
    [session, client, perms, props.signOut, props.switchTenant]
  );
  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within a SessionProvider");
  return ctx;
}

/** Permission check for gating write controls. */
export function useCan(p: Permission): boolean {
  return useSession().can(p);
}
